import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/auth";
import { logError } from "@/lib/log";

const emptyForm = { username: "", password: "", group: "desfiemer" };

export default function GestoresManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/admins");
      setItems(res.data || []);
    } catch (e) {
      logError("No se pudieron cargar los gestores", e);
      toast.error("No se pudieron cargar los gestores.");
    } finally {
      setLoading(false);
    }
    // adminApi/logError/toast son imports estables; setters de useState
    // tambien lo son. Lint silenciado intencionalmente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (g) => {
    setEditingId(g.id);
    setForm({ username: g.username, password: "", group: g.group });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingId) {
      if (!form.username.trim() || !form.password) {
        toast.error("Correo y contraseña son obligatorios.");
        return;
      }
      if (!/^.+@.+\..+$/.test(form.username.trim())) {
        toast.error("El correo no tiene un formato válido.");
        return;
      }
    }
    setSaving(true);
    try {
      if (editingId) {
        const payload = { group: form.group };
        if (form.password) payload.password = form.password;
        await adminApi.put(`/admins/${editingId}`, payload);
        toast.success("Gestor actualizado.");
      } else {
        await adminApi.post(`/admins`, {
          username: form.username.trim(),
          password: form.password,
          group: form.group,
        });
        toast.success("Gestor creado.");
      }
      setDialogOpen(false);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar gestor.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminApi.delete(`/admins/${deleteTarget.id}`);
      toast.success("Gestor eliminado.");
      setDeleteTarget(null);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo eliminar.");
    }
  };

  return (
    <section data-testid="gestores-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="heading-font text-2xl font-bold text-zinc-950">
            Gestores
          </h2>
          <p className="text-sm text-zinc-600 mt-1">
            {items.length} {items.length === 1 ? "cuenta" : "cuentas"} ·
            Alta, baja y modificación de gestores.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="h-11 px-5 bg-zinc-950 hover:bg-zinc-800 text-white rounded-md"
          data-testid="new-gestor-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuevo gestor
        </Button>
      </div>

      {loading ? (
        <div className="bg-white border border-zinc-200 rounded-lg p-10 text-center text-zinc-500">
          Cargando…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center">
          <h3 className="heading-font text-xl font-bold text-zinc-950">
            No hay gestores
          </h3>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((g) => (
            <div
              key={g.id}
              className="bg-white border border-zinc-200 rounded-lg p-5 transition-colors hover:border-zinc-400 flex items-center justify-between gap-4"
              data-testid={`gestor-row-${g.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold text-zinc-950 truncate">
                    {g.username}
                  </div>
                  {/* Badge de grupo eliminado: sólo existen gestores DESFIEMER. */}
                  {g.is_super_admin && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] uppercase tracking-wider rounded bg-black text-white"
                      title="Super-administrador"
                    >
                      <ShieldCheck className="h-3 w-3" />
                      Super-admin
                    </span>
                  )}
                  {g.seeded && !g.is_super_admin && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] uppercase tracking-wider rounded bg-zinc-50 text-zinc-500 border border-zinc-200"
                      title="Sembrado desde configuración de servidor"
                    >
                      <Lock className="h-3 w-3" />
                      Sembrado
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEdit(g)}
                  className="h-9 border border-zinc-300 hover:bg-zinc-50"
                  data-testid={`edit-gestor-${g.id}`}
                  aria-label="Editar gestor"
                  title="Editar"
                  disabled={g.is_super_admin && g.group !== form.group ? false : false}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDeleteTarget(g)}
                  className="h-9 border border-zinc-300 hover:bg-red-50 hover:border-red-300 hover:text-red-700 disabled:opacity-40"
                  data-testid={`delete-gestor-${g.id}`}
                  aria-label="Eliminar gestor"
                  title={g.seeded || g.is_super_admin
                    ? "Sembrado desde servidor — no se puede eliminar desde aquí"
                    : "Eliminar"}
                  disabled={g.is_super_admin || g.seeded}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="gestor-dialog">
          <DialogHeader>
            <DialogTitle className="heading-font text-2xl">
              {editingId ? "Editar gestor" : "Nuevo gestor"}
            </DialogTitle>
            <DialogDescription>
              {editingId
                ? "Cambia la contraseña o el grupo. El correo no se puede modificar."
                : "Da de alta un gestor. El correo será su usuario de acceso."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Correo electrónico {editingId ? "" : "*"}
              </label>
              <Input
                value={form.username}
                onChange={(e) =>
                  setForm({ ...form, username: e.target.value })
                }
                disabled={!!editingId}
                placeholder="usuario@empresa.com"
                className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                data-testid="gestor-username-input"
                autoFocus={!editingId}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Contraseña {editingId ? "(deja vacío para mantener)" : "*"}
              </label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) =>
                  setForm({ ...form, password: e.target.value })
                }
                placeholder={editingId ? "Nueva contraseña…" : "Contraseña"}
                className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                data-testid="gestor-password-input"
              />
            </div>
            {/* Selector de Grupo ELIMINADO: sólo existen gestores DESFIEMER.
                El backend sigue aceptando el campo `group` por compatibilidad
                pero el frontend siempre envía "desfiemer". */}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={saving}
              className="border border-zinc-300"
              data-testid="gestor-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="gestor-save-button"
            >
              {saving ? "Guardando…" : editingId ? "Guardar cambios" : "Crear gestor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent data-testid="delete-gestor-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar gestor?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará{" "}
              <span className="font-semibold text-zinc-950">
                {deleteTarget?.username}
              </span>
              .
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-gestor-cancel">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-gestor-confirm"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
